import React from 'react';
import { useDispatch } from 'react-redux';
import { DragDropContext, Droppable, type DragStart, type DropResult } from 'react-beautiful-dnd';
import { Box, CircularProgress } from '@mui/material';
import { useDeepCompareEffect } from '@fuse/hooks';

import { executeStored } from 'app/main/components/Utils';
import useSettings from 'app/main/components/hooks/useSettings';
import useUserData from 'app/main/components/hooks/useUserData';
import { type MiddlewhereFilter } from 'app/main/components/filter/FilterItemManager';

/* eslint-disable-next-line import/no-cycle */
import useViewState from '../components/useViewState';
import BoardList, { type BoardListType } from './components/BoardList';
import BoardFab from './components/BoardFab';

interface KanbanBoardProps {
	id?: string;
	module: string;
	bucket: string;
	defaultFilters?: MiddlewhereFilter[];
	baseModel?: any;
}

export default function KanbanBoard({ module, bucket, defaultFilters, id, baseModel }: KanbanBoardProps) {
	const dispatch = useDispatch();
	const { settings } = useSettings(module);
	const { settings: bucketSettings } = useSettings(bucket);
	const userData = useUserData();

	const [lists, setLists] = React.useState<BoardListType[]>([]);
	const [draggedCard, setDraggedCard] = React.useState<any>(null);

	const { paginationProps } = useViewState({
		id,
		module,
		filter: {
			defaultFilters: [
				...(defaultFilters || []),
				{ filt_field: `${settings.modu_short}_deleted`, filt_clause: 'not_equal', filt_value: 1 }
			]
		}
	});

	const { filterProps, orderProps, paginatorProps } = paginationProps;

	const [refresh, setRefresh] = React.useState(true);
	const updateRefresh = () => setRefresh(!refresh);

	useDeepCompareEffect(() => {
		const controller = new AbortController();

		paginatorProps.setIsLoading(true);
		executeStored({
			action: 'st_sys_kanban_list',
			params: {
				module,
				bucket,
				filters: filterProps?.filters,
				query: filterProps?.query
			},
			dispatch,
			signal: controller.signal,
			successHandler: response => {
				setLists(response.data);
				paginatorProps.setIsLoading(false);
			}
		});

		return () => {
			controller.abort();
		};
	}, [dispatch, filterProps?.filters, filterProps?.query, refresh]);

	function updateCard(newListId: string, newCard) {
		setLists(state => {
			state.find(list => `${list.id}` === newListId)?.cards?.push(newCard);
			return [...state];
		});
	}

	function handleDragStart({ source }: DragStart) {
		const draggedCardList = lists.find(list => `${list.id}` === source.droppableId);
		const newDraggedCard = draggedCardList?.cards?.[source.index];
		setDraggedCard(newDraggedCard);
	}

	function handleDragEnd({ draggableId, type, source, destination }: DropResult) {
		// dropped nowhere
		if (!destination) {
			setDraggedCard(null);
			return;
		}

		// did not move anywhere - can bail early
		if (source.droppableId === destination.droppableId) {
			setDraggedCard(null);
			return;
		}

		// move card
		if (type === 'card') {
			// remove card from old list
			setLists(state => {
				state.find(list => `${list.id}` === source.droppableId)?.cards?.splice(source.index, 1);
				return [...state];
			});

			executeStored({
				action: `st_${module}_update`,
				dispatch,
				data: {
					[`${settings.modu_short}_uid`]: draggableId,
					[`${settings.modu_short}_user_modified_uid`]: userData.user_uid,
					[`${settings.modu_short}_${bucketSettings.modu_short}_id`]: destination.droppableId
				},
				successHandler: response => {
					// move card to new list
					updateCard(destination.droppableId, response.data);
				},
				errorHandler: () => {
					// reset card to old list
					updateCard(source.droppableId, draggedCard);
				}
			});

			setDraggedCard(null);
		}
	}

	return (
		<Box className="flex h-full flex-1">
			{!paginatorProps.isLoading ? (
				<DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
					<Droppable droppableId="list" type="list" direction="horizontal">
						{provided => (
							<div ref={provided.innerRef} className="container flex pr-80">
								{lists?.map(list => (
									<BoardList
										key={list.id}
										module={module}
										list={list}
										baseModel={baseModel}
										draggedCard={draggedCard}
										updateRefresh={updateRefresh}
									/>
								))}
								{provided.placeholder}
							</div>
						)}
					</Droppable>
					<BoardFab module={module} filterProps={filterProps} />
				</DragDropContext>
			) : (
				<CircularProgress className="m-auto h-60 w-60" disableShrink />
			)}
		</Box>
	);
}
